import { Dispatch } from 'redux';
import {
  queryMaintenance,
  queryMaintenanceForWorkpacks,
  removeMaintenance,
  updateMultipleMxItemTolerance,
} from '../../services/api';
import { NoContentResponse } from '..';
import { getPublicScheduledMxItem } from '../../services/external';
import { WorkpacksActionTypes } from '../workpacks';
import { Maintenance, MaintenanceActionTypes } from '.';

// Response type for maintenance items query
export interface MxItemsResponse {
  scheduled_mx_items: Maintenance[];
  draft_items?: Maintenance[];
  total: number;
  meta?: {
    per_page: number;
    current_page: number;
    total_pages: number;
  };
}

interface MaintenanceQueryParams {
  aircraft_id?: string;
  page?: number;
  per?: number;
  start_date?: string | null;
  end_date?: string | null;
  search_term?: string | null;
  status?: string | string[];
  item_type?: string | string[];
  area?: string | string[];
  source?: string | string[];
  limits?: string | string[];
  planning?: string | string[];
  tolerance?: string | string[];
  id?: string;
}

interface MaintenancePayload {
  payload: MaintenanceQueryParams;
  skipDeleteAll?: boolean;
  forceReplace?: boolean;
}

interface WorkpacksPayload {
  payload: {
    aircraft_id: string;
    status: string[];
  };
  skipDeleteAll?: boolean;
}

interface PublicScheduledMxItemPayload {
  payload: {
    id: string;
    key: string;
  };
}

interface MxItemPayload {
  payload: Maintenance;
}

export const getAllMxItems =
  ({ payload, skipDeleteAll, forceReplace }: MaintenancePayload) =>
  async (dispatch: Dispatch): Promise<MxItemsResponse | null> => {
    if (!skipDeleteAll) {
      dispatch({
        type: MaintenanceActionTypes.DELETE_ALL,
        payload: null,
      });
    }
    const response = await queryMaintenance(payload);
    if (forceReplace) {
      dispatch({
        type: MaintenanceActionTypes.REPLACE_ALL,
        payload: response.scheduled_mx_items,
      });
      dispatch({
        type: MaintenanceActionTypes.REPLACE_ALL_ACTIVE,
        payload: response.scheduled_mx_items,
      });
      dispatch({
        type: MaintenanceActionTypes.REPLACE_ALL_DRAFT,
        payload: response.draft_items,
      });
    } else {
      dispatch({
        type: MaintenanceActionTypes.SAVE_ALL,
        payload: response.scheduled_mx_items,
      });
      dispatch({
        type: MaintenanceActionTypes.SAVE_ALL_ACTIVE,
        payload: response.scheduled_mx_items,
      });
      return response;
    }
    return null;
  };

export const getAllMxItemsWorkpacks =
  ({ payload, skipDeleteAll }: WorkpacksPayload) =>
  async (dispatch: Dispatch): Promise<null> => {
    if (!skipDeleteAll) {
      dispatch({
        type: MaintenanceActionTypes.DELETE_ALL,
        payload: null,
      });
    }
    const response = await queryMaintenanceForWorkpacks(payload);
    if (response) {
      dispatch({
        type: MaintenanceActionTypes.SAVE_ALL,
        payload: response.body,
      });
    }
    return null;
  };

export const getPublicScheduledMxItemDetails =
  ({ payload }: PublicScheduledMxItemPayload) =>
  async (dispatch: Dispatch): Promise<void> => {
    const response = await getPublicScheduledMxItem(payload);
    // Despite being a defect API endpoint and it returns a workpack so we need to save
    // the against the workpack model/store.
    dispatch({
      type: WorkpacksActionTypes.SAVE_PUBLIC,
      payload: response,
    });
  };

export const removeMxItem =
  ({ payload }: { payload: { id: string; aircraft_id?: string } }) =>
  async (dispatch: Dispatch): Promise<NoContentResponse> => {
    try {
      const response = await removeMaintenance(payload);
      await queryMaintenance(payload);
      dispatch({
        type: MaintenanceActionTypes.DELETE,
        payload,
      });
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const updateMxItemSkipAPI =
  ({ payload }: MxItemPayload) =>
  async (dispatch: Dispatch): Promise<null> => {
    dispatch({
      type: MaintenanceActionTypes.UPDATE_SINGLE_ACTIVE,
      payload,
    });
    return null;
  };

export const updateScheduledMxItemsTolerances =
  (payload: { items: { id: string; attributes: { [key: string]: unknown } }[]; aircraft_id: string }) =>
  async (dispatch: Dispatch): Promise<void> => {
    const response = await updateMultipleMxItemTolerance(payload);
    dispatch({
      type: MaintenanceActionTypes.UPDATE_MULTIPLE_TOLERANCE,
      payload: response,
    });
  };
